import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { DollarSign, Users, TrendingUp, Wallet, RefreshCw, AlertCircle, Sparkles } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardValue, CardSubtitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { api } from '@/lib/api';
import { formatNumber } from '@/lib/utils';
import { CONSERVATIVE_TARGETS, conversionTarget, CHURN_TARGET_PCT } from '@/lib/targets';

interface Kpis {
  mau_30d: number;
  dau_30d: number;
  dau_mau_ratio: number;
  pro_subscribers_active: number;
  pro_subscribers_new_30d: number;
  pro_subscribers_cancelled_30d: number;
  mrr_brl_gross: number;
  mrr_brl_net: number;
  mrr_b2b_brl: number | null;
  mrr_total_brl: number;
  mrr_b2b_disponivel: boolean;
  conversion_pct: number;
  churn_pct_30d: number;
  arpu_brl: number;
  ltv_brl_estimated: number | null;
  infra_cost_brl_30d: number;
  infra_cost_breakdown: Record<string, number>;
  cash_balance_brl: number;
  runway_months: number | null;
  storage_r2_gb: number;
  k_factor_30d: number;
  deep_link_signups_30d: number;
}

interface Escolas {
  proViaEscola: {
    total: number;
    porPapel: { student: number; teacher: number };
    porEscola: Array<{
      id: string; nome: string; plano: string; estado: string;
      alunos: number; professores: number; total: number;
    }>;
    naoEntraEm: string[];
  };
  escolas: {
    total: number;
    pagantes: number;
    porPlano: Record<string, number>;
    porEstado: Record<string, number>;
    planosConhecidos: string[];
  };
  mrrB2b: { centavos: number | null; disponivel: boolean; motivo: string };
}

interface HistoricoPoint {
  month: string;
  mau_30d: number;
  pro_active: number;
  mrr_brl_net: number;
  conversion_pct: number;
  churn_pct_30d: number;
  infra_cost_brl_30d: number;
}

interface ProFeatureRow {
  feature: string;
  upsell_views: number;
  conversions: number;
  rate_pct: number;
}

interface B2bStage {
  name: string;
  count: number;
  order: number;
}

const BRL = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const FEATURE_LABELS: Record<string, string> = {
  stats:        'Stats avançadas',
  download:     'Download offline',
  upload_hq:    'Upload HQ',
  player_adv:   'Player avançado',
  identidade:   'Identidade visual',
  posts_pinned: 'Posts fixados',
};

// Consentimento de comunicação de marketing.
// Spec: Drive/Monetizacao/Spec_MarketingOptInCounter_v1.md
//
// Os três estados são separados de propósito e NÃO podem ser colapsados:
//   aceitou       — respondeu e disse sim
//   recusou       — respondeu e disse não
//   naoPerguntado — a conta nunca foi perguntada NO SERVIDOR
//
// A distinção entre os dois últimos é a diferença entre "medimos e a resposta
// foi não" e "nunca medimos".
type MarketingOptIn = {
  periodo_dias: number;
  total: {
    aceitou: number;
    recusou: number;
    nao_perguntado: number;
    base_total: number;
    responderam: number;
    taxa_aceite_pct: number | null;
  };
  periodo: {
    aceitou: number;
    recusou: number;
    responderam: number;
    taxa_aceite_pct: number | null;
  };
};

function useMonet<T = unknown>(name: string, params?: Record<string, string | number>) {
  return useQuery<{ data: T; fromCache: boolean }>({
    queryKey: [`monet-${name}`, params || {}],
    queryFn: async () => {
      const res = await api.get(`/admin/monetizacao/${name}`, { params });
      return res.data;
    },
  });
}

export function Monetizacao() {
  const [compareToTargets, setCompareToTargets] = useState(true);
  const [optInDias, setOptInDias] = useState(30);
  const months = 12;

  const kpis      = useMonet<Kpis>('kpis');
  const historico = useMonet<{ months: number; series: HistoricoPoint[] }>('historico', { months });
  const features  = useMonet<ProFeatureRow[]>('pro/features');
  const pipeline  = useMonet<{ stages: B2bStage[] }>('b2b/pipeline');
  const optIn     = useMonet<MarketingOptIn>('marketing-opt-in', { days: optInDias });
  const escolas   = useMonet<Escolas>('escolas');

  const k = kpis.data?.data;
  const series = historico.data?.data?.series ?? [];

  // Combina série real com targets do modelo (mesma ordem mensal)
  const seriesWithTargets = series.map((p, i) => {
    const t = CONSERVATIVE_TARGETS[i];
    return {
      ...p,
      target_mau:        t?.mau ?? null,
      target_conversion: conversionTarget(i + 1),
      target_churn:      CHURN_TARGET_PCT,
    };
  });

  const mauTarget  = CONSERVATIVE_TARGETS[0]?.mau ?? 100;

  return (
    <Layout
      title="Monetização"
      actions={
        <>
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={compareToTargets}
              onChange={(e) => setCompareToTargets(e.target.checked)}
              className="rounded border-border"
            />
            Comparar com modelo conservador
          </label>
          <Button variant="ghost" size="sm" onClick={() => window.location.reload()}>
            <RefreshCw size={14} /> Atualizar
          </Button>
        </>
      }
    >
      {/* === Topo: 5 KPIs principais === */}
      <section className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        <Card>
          <CardHeader>
            <CardTitle>MAU (30d)</CardTitle>
            <Users size={18} className="text-brand" />
          </CardHeader>
          <CardValue>{formatNumber(k?.mau_30d)}</CardValue>
          <CardSubtitle>
            {compareToTargets
              ? `${pctVs(k?.mau_30d ?? 0, mauTarget)}% do target M1 (${formatNumber(mauTarget)})`
              : `DAU/MAU ${((k?.dau_mau_ratio || 0) * 100).toFixed(1)}%`}
          </CardSubtitle>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Pro ativos</CardTitle>
            <Sparkles size={18} className="text-brand" />
          </CardHeader>
          <CardValue>{formatNumber(k?.pro_subscribers_active)}</CardValue>
          <CardSubtitle>
            +{formatNumber(k?.pro_subscribers_new_30d)} novos · −{formatNumber(k?.pro_subscribers_cancelled_30d)} cancelaram (30d)
          </CardSubtitle>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>MRR líquido</CardTitle>
            <DollarSign size={18} className="text-brand" />
          </CardHeader>
          <CardValue>{BRL(k?.mrr_brl_net || 0)}</CardValue>
          <CardSubtitle>ARPU {BRL(k?.arpu_brl || 0)}</CardSubtitle>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Burn rate</CardTitle>
            <TrendingUp size={18} className="text-brand" />
          </CardHeader>
          <CardValue>{BRL(k?.infra_cost_brl_30d || 0)}</CardValue>
          <CardSubtitle>Railway · R2 · Resend · PostHog</CardSubtitle>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Runway</CardTitle>
            <Wallet size={18} className="text-brand" />
          </CardHeader>
          <CardValue>
            {k?.runway_months != null ? `${k.runway_months} ${k.runway_months === 1 ? 'mês' : 'meses'}` : '—'}
          </CardValue>
          <CardSubtitle>Caixa: {BRL(k?.cash_balance_brl || 0)}</CardSubtitle>
        </Card>
      </section>

      {/* Avisos contextuais */}
      {k && k.cash_balance_brl === 0 && k.infra_cost_brl_30d === 0 && (
        <Card className="mt-6 border-brand-border bg-brand-bg">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="mt-0.5 flex-shrink-0 text-brand" />
            <div className="text-sm text-text-primary">
              Burn rate e caixa ainda não preenchidos. Adicione uma row na tabela{' '}
              <code className="rounded bg-bg-input px-1 text-brand">company_finances</code> (mensal) pra ativar
              runway e gráficos de custo.
            </div>
          </div>
        </Card>
      )}

      {k && k.pro_subscribers_active === 0 && (
        <Card className="mt-3 border-border bg-bg-elevated">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="mt-0.5 flex-shrink-0 text-text-muted" />
            <div className="text-sm text-text-secondary">
              Nenhuma assinatura Pro ativa. KPIs de MRR / conversão / churn ficam zerados até a integração
              com RevenueCat popular a tabela <code className="rounded bg-bg-input px-1">pro_subscriptions</code>.
            </div>
          </div>
        </Card>
      )}

      {/* === Gráficos === */}
      <section className="mt-6 grid grid-cols-1 gap-4">
        <ChartCard title="MAU growth (real vs conservador)">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={seriesWithTargets}>
              <CartesianGrid stroke="#2A2A2A" strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="#737373" fontSize={12} />
              <YAxis stroke="#737373" fontSize={12} />
              <Tooltip {...DARK_TOOLTIP} />
              <Legend wrapperStyle={{ fontSize: 12, color: '#A3A3A3' }} />
              <Line type="monotone" dataKey="mau_30d" name="MAU real" stroke="#C084FC" strokeWidth={2} dot={false} />
              {compareToTargets && (
                <Line type="monotone" dataKey="target_mau" name="MAU target" stroke="#737373"
                      strokeWidth={2} strokeDasharray="5 5" dot={false} />
              )}
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Conversão Pro % (real vs target da fase)">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={seriesWithTargets}>
              <CartesianGrid stroke="#2A2A2A" strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="#737373" fontSize={12} />
              <YAxis stroke="#737373" fontSize={12} unit="%" />
              <Tooltip {...DARK_TOOLTIP} />
              <Legend wrapperStyle={{ fontSize: 12, color: '#A3A3A3' }} />
              <Line type="monotone" dataKey="conversion_pct" name="Conversão real" stroke="#C084FC" strokeWidth={2} dot={false} />
              {compareToTargets && (
                <Line type="monotone" dataKey="target_conversion" name="Conversão target" stroke="#737373"
                      strokeWidth={2} strokeDasharray="5 5" dot={false} />
              )}
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Churn Pro % (real vs 5% assumido)">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={seriesWithTargets}>
              <CartesianGrid stroke="#2A2A2A" strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="#737373" fontSize={12} />
              <YAxis stroke="#737373" fontSize={12} unit="%" />
              <Tooltip {...DARK_TOOLTIP} />
              <Legend wrapperStyle={{ fontSize: 12, color: '#A3A3A3' }} />
              <Line type="monotone" dataKey="churn_pct_30d" name="Churn real" stroke="#C084FC" strokeWidth={2} dot={false} />
              {compareToTargets && (
                <Line type="monotone" dataKey="target_churn" name="Churn target (5%)" stroke="#737373"
                      strokeWidth={2} strokeDasharray="5 5" dot={false} />
              )}
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>

      {/* === Onboarding: consentimento de comunicação === */}
      <section className="mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Consentimento de comunicação</CardTitle>
            <div className="flex gap-1">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => setOptInDias(d)}
                  className={`rounded px-2 py-1 text-xs ${
                    optInDias === d
                      ? 'bg-brand/15 text-brand'
                      : 'text-text-muted hover:text-text-secondary'
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </CardHeader>

          {(() => {
            const o = optIn.data?.data;
            if (!o) return <CardValue>—</CardValue>;

            const t = o.total;
            // Ninguém respondeu ainda: mostrar "0%" leria como rejeição total,
            // então mostra travessão. A taxa vem null do backend nesse caso.
            const taxa = t.taxa_aceite_pct;

            return (
              <>
                <CardValue>{taxa === null ? '—' : `${taxa.toFixed(2)}%`}</CardValue>
                <CardSubtitle>
                  {formatNumber(t.aceitou)} aceitaram · {formatNumber(t.recusou)} recusaram
                  {t.responderam > 0 && ` · ${formatNumber(t.responderam)} responderam`}
                </CardSubtitle>

                <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                  <div className="rounded-lg bg-bg-elevated p-3">
                    <div className="text-lg font-semibold text-text-primary">{formatNumber(t.aceitou)}</div>
                    <div className="text-xs text-text-muted">aceitaram</div>
                  </div>
                  <div className="rounded-lg bg-bg-elevated p-3">
                    <div className="text-lg font-semibold text-text-primary">{formatNumber(t.recusou)}</div>
                    <div className="text-xs text-text-muted">recusaram</div>
                  </div>
                  <div className="rounded-lg bg-bg-elevated p-3">
                    <div className="text-lg font-semibold text-text-secondary">{formatNumber(t.nao_perguntado)}</div>
                    <div className="text-xs text-text-muted">não perguntado</div>
                  </div>
                </div>

                {o.periodo.responderam > 0 && (
                  <div className="mt-3 text-xs text-text-secondary">
                    Nos últimos {o.periodo_dias} dias: {formatNumber(o.periodo.aceitou)} aceitaram ·{' '}
                    {formatNumber(o.periodo.recusou)} recusaram
                    {o.periodo.taxa_aceite_pct !== null && ` (${o.periodo.taxa_aceite_pct.toFixed(2)}%)`}
                  </div>
                )}

                {/* Sem esta nota, "não perguntado" alto seria lido como defeito
                    ou como rejeição em massa — quando na verdade é a base
                    inteira anterior a 06/09/2026, que nunca foi registrada no
                    servidor. Não há migração retroativa: o dado estava nos
                    aparelhos, e inventar um default seria fabricar consentimento. */}
                {t.nao_perguntado > 0 && (
                  <div className="mt-3 flex gap-2 rounded-lg bg-bg-elevated p-3 text-xs text-text-muted">
                    <AlertCircle size={14} className="mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-text-secondary">&quot;Não perguntado&quot; alto é esperado.</strong>{' '}
                      Até 06/09/2026 essa resposta ficava só no aparelho e o servidor nunca soube — toda a
                      base anterior cai aqui, e não há como migrar (o dado estava nos celulares). O número
                      começa a andar quando o app passar a enviar o campo. A taxa acima é sobre quem
                      respondeu, não sobre a base.
                    </span>
                  </div>
                )}
              </>
            );
          })()}
        </Card>
      </section>

      {/* === Tabelas === */}
      <section className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Top features Pro (upsells)</CardTitle>
          </CardHeader>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-text-muted">
                <th className="py-2 font-medium">Feature</th>
                <th className="py-2 text-right font-medium">Views</th>
                <th className="py-2 text-right font-medium">Conv.</th>
                <th className="py-2 text-right font-medium">Rate</th>
              </tr>
            </thead>
            <tbody>
              {(features.data?.data || []).map((f) => (
                <tr key={f.feature} className="border-b border-border last:border-0">
                  <td className="py-2 text-text-primary">{FEATURE_LABELS[f.feature] || f.feature}</td>
                  <td className="py-2 text-right text-text-secondary">{formatNumber(f.upsell_views)}</td>
                  <td className="py-2 text-right text-text-secondary">{formatNumber(f.conversions)}</td>
                  <td className="py-2 text-right text-brand">{f.rate_pct.toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-text-muted">
            Instrumentar evento <code className="rounded bg-bg-input px-1">pro_upsell_clicked</code> no mobile pra ativar.
          </p>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Pipeline B2B</CardTitle>
          </CardHeader>
          <ul className="space-y-1">
            {(pipeline.data?.data?.stages || []).map((s) => (
              <li key={s.name} className="flex items-center justify-between border-b border-border py-2 last:border-0">
                <span className="text-text-secondary">{s.name}</span>
                <span className="font-semibold text-brand">{formatNumber(s.count)}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-text-muted">
            Editar via commit em <code className="rounded bg-bg-input px-1">src/data/b2b-pipeline.json</code>.
          </p>
        </Card>
      </section>

      {/* ═══ ESCOLAS — Pro herdado e escolas pagantes ═══

          🔴 SEPARADO DO MRR B2C DE PROPÓSITO. Herança de escola não cria linha
          em `pro_subscriptions`, e é de lá que saem assinante, conversão,
          churn e MRR. As pessoas listadas aqui NÃO são assinantes, e o painel
          diz isso em voz alta — senão alguém soma os dois números um dia. */}
      <section className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Pro via escola</CardTitle>
            <Sparkles size={18} className="text-brand" />
          </CardHeader>
          <CardValue>{formatNumber(escolas.data?.data?.proViaEscola?.total ?? 0)}</CardValue>
          <CardSubtitle>
            {formatNumber(escolas.data?.data?.proViaEscola?.porPapel?.student ?? 0)} aluno(s) ·{' '}
            {formatNumber(escolas.data?.data?.proViaEscola?.porPapel?.teacher ?? 0)} professor(es)
          </CardSubtitle>

          <p className="mt-3 rounded bg-bg-input px-2 py-1 text-xs text-text-muted">
            ⚠️ Não entram em assinantes, conversão, churn nem MRR do Pro.
          </p>

          {(escolas.data?.data?.proViaEscola?.porEscola || []).length > 0 && (
            <ul className="mt-3 space-y-1">
              {(escolas.data?.data?.proViaEscola?.porEscola || []).map((e) => (
                <li key={e.id} className="flex items-center justify-between border-b border-border py-2 last:border-0">
                  <span className="text-text-secondary">
                    {e.nome} <span className="text-text-muted">({e.plano} · {e.estado})</span>
                  </span>
                  <span className="font-semibold text-brand">{formatNumber(e.total)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Escolas pagantes</CardTitle>
            <DollarSign size={18} className="text-brand" />
          </CardHeader>
          <CardValue>{formatNumber(escolas.data?.data?.escolas?.pagantes ?? 0)}</CardValue>
          <CardSubtitle>
            de {formatNumber(escolas.data?.data?.escolas?.total ?? 0)} escola(s) no total
          </CardSubtitle>

          <ul className="mt-3 space-y-1">
            {Object.entries(escolas.data?.data?.escolas?.porEstado || {}).map(([estado, n]) => (
              <li key={estado} className="flex items-center justify-between border-b border-border py-2 last:border-0">
                <span className="text-text-secondary">{estado}</span>
                <span className="font-semibold">{formatNumber(n as number)}</span>
              </li>
            ))}
          </ul>

          {/* ⚠️ `null` não é zero. Zero diria "medimos e deu zero"; a ausência
              diz "não há o que medir". A cobrança de escola ainda não existe. */}
          <div className="mt-4 border-t border-border pt-3">
            <p className="text-sm text-text-secondary">
              MRR B2B:{' '}
              <span className="font-semibold">
                {escolas.data?.data?.mrrB2b?.disponivel
                  ? BRL((escolas.data?.data?.mrrB2b?.centavos ?? 0) / 100)
                  : '—'}
              </span>
            </p>
            {!escolas.data?.data?.mrrB2b?.disponivel && (
              <p className="mt-1 text-xs text-text-muted">
                {escolas.data?.data?.mrrB2b?.motivo}
              </p>
            )}
            <p className="mt-2 text-sm text-text-secondary">
              MRR total:{' '}
              <span className="font-semibold text-brand">{BRL(k?.mrr_total_brl || 0)}</span>{' '}
              <span className="text-xs text-text-muted">
                (B2C {BRL(k?.mrr_brl_net || 0)}{k?.mrr_b2b_disponivel ? '' : ' · B2B ainda não medido'})
              </span>
            </p>
          </div>
        </Card>
      </section>
    </Layout>
  );
}

function pctVs(actual: number, target: number): number {
  if (target === 0) return 0;
  return +((actual / target) * 100).toFixed(1);
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <div>{children}</div>
    </Card>
  );
}

const DARK_TOOLTIP = {
  contentStyle: {
    background: '#1A1A1A',
    border: '1px solid #2A2A2A',
    borderRadius: 8,
    fontSize: 12,
  },
  labelStyle: { color: '#F5F5F5' },
  itemStyle:  { color: '#A3A3A3' },
};
